import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';

import copy from 'copy-to-clipboard';
import { Copy, GripVertical, KeyRound, Pencil, Trash } from 'lucide-react';
import { DefaultTooltip, Label, Input, cn, Badge } from 'twyne-new.ui';

import { ControlledConfirmationPopover } from '@/components/ConfirmationPopover';
import { PureSwitch } from '@/components/form/Switch';
import { useAppAlert } from '@Components/AppAlert';
import { useGrid } from '@Components/Grid/context';
import InfoTip from '@Components/InfoTip';
import { formatDateTime, formatDate } from '@Utils/index';

import { useDragContext } from './Sortable/DragContext';

export function keyOf(info, prefix = info.column.id) {
  return `${prefix}-${info.row.original.id}`
}

export function getInfo(info) {
  const { row: { original: row }, column: { id: field } } = info

  return {
    row, field,
    get value() {
      return info.getValue()
    },
  }
}

export function Toggle({ info }) {
  const { row, field, value } = getInfo(info)
  const { definitions: { entityName }, events: { handleUpdateEntry } } = useGrid()
  const [updating, setUpdating] = useState(false)
  const { handleSuccess } = useAppAlert()

  const handleUpdateActive = useCallback(() => {
    setUpdating(true)
    handleUpdateEntry({ id: row.id, [field]: !value }).finally(() => setUpdating(false))
    handleSuccess(`${entityName} edited!`)
  }, [handleUpdateEntry, field, row, value, handleSuccess, entityName])

  return <PureSwitch value={updating ? !value : value} onChange={handleUpdateActive} disabled={updating} />
}

export function DragHandlerFormatter({ info }) {
  const id = keyOf(info, 'drag-handler')
  const { attributes, listeners, isDragging } = useDragContext()

  return <span id={id} className="flex items-center justify-center hover:twy-text-base-accent" {...attributes} {...listeners}>
    <GripVertical size={16} style={{ cursor: isDragging ? 'grabbing' : 'grab' }} />
  </span>
}

export function BooleanFormatter({ info }) {
  const { value } = getInfo(info)
  return <Badge variant='outline' color={value ? 'success' : 'danger'}>{value ? 'Active' : 'Inactive'}</Badge>
}

export function Actions({ formatters, info }) {
  return <div className='flex justify-end gap-2'>
    {formatters.map((formatter, index) => {
      const element = formatter(info)
      if (!element) return null
      return <div
        className={index === 0 ? 'pl-3 border-l twy-border-base' : ''}
        key={keyOf(info, formatter.name)}>
        {element}
      </div>
    })}
  </div>
}

export function EditEntity({ info }) {
  const { row } = getInfo(info)
  const { definitions: { basePath, entityNameModule, entityNamePlural } } = useGrid()

  const path = useMemo(() => {
    if (basePath) return basePath;
    return `/${entityNameModule}/${entityNamePlural.toLowerCase()}`
  }, [basePath, entityNameModule, entityNamePlural])

  return <>
    <DefaultTooltip content='Edit'>
      <Link to={`${path}/${row.id}`} className='action'>
        <Pencil size={16} />
      </Link>
    </DefaultTooltip>
  </>
}

export function DeleteEntity({ info }) {
  const { row } = getInfo(info)
  const { definitions: { entityName }, events: { handleDeleteEntry } } = useGrid()
  const { handleSuccess } = useAppAlert()

  const handleSubmit = useCallback(async () => {
    await handleDeleteEntry(row)
    handleSuccess(`${entityName} deleted!`)
  }, [row, handleDeleteEntry, handleSuccess, entityName])

  return <>
    <ControlledConfirmationPopover
      onSubmit={handleSubmit}
      title={<span>Delete {entityName.toLowerCase()} <i>{`[#${row.id}] ${row.name || row.description || ''}`}</i></span>}
      target={<DefaultTooltip content='Delete'><Trash size={16} /></DefaultTooltip>}
    >
      <p><strong>Are you sure you want do this?</strong></p>
    </ControlledConfirmationPopover>
  </>
}

export function DuplicateEntity({ info, asLabel }) {
  const { row } = getInfo(info)
  const { definitions: { entityName }, events: { handleDuplicateEntry } } = useGrid()
  const { handleSuccess } = useAppAlert()

  const handleSubmit = useCallback(async () => {
    await handleDuplicateEntry(row)
    handleSuccess(`${entityName} duplicated!`)
  }, [row, handleDuplicateEntry, handleSuccess, entityName])

  const target = asLabel ? <div>Duplicate</div> : <DefaultTooltip content='Duplicate'><Copy size={16} /></DefaultTooltip>

  return <>
    <ControlledConfirmationPopover
      onSubmit={handleSubmit}
      title={<span>Duplicate {entityName.toLowerCase()} <i>{`[#${row.id}] ${row.name || row.description}`}</i></span>}
      target={target}
    >
      <div className='mt-2 font-bold'>Are you sure you want duplicate this entry?</div>
    </ControlledConfirmationPopover>
  </>
}

export function DuplicateEntityWithName({ info }) {
  const { row } = getInfo(info)
  const { definitions: { entityName }, events: { handleDuplicateEntry } } = useGrid()
  const { handleSuccess } = useAppAlert()

  const [name, setName] = useState('')

  const handleSubmit = useCallback(async () => {
    await handleDuplicateEntry({ ...row, name })
    handleSuccess(`${entityName} duplicated!`)
  }, [row, name, handleDuplicateEntry, handleSuccess, entityName])

  return <>
    <ControlledConfirmationPopover
      target={<DefaultTooltip content='Duplicate'><Copy size={16} /></DefaultTooltip>}
      title={<span>Duplicate {entityName.toLowerCase()} <i>{`[#${row.id}] ${row.name}`}</i></span>}
      onSubmit={handleSubmit}
    >
      <Label>New {entityName.toLowerCase()} Name</Label>
      <Input required name="name" value={name} onChange={event => setName(event.target.value)} />
      <div className='mt-2 font-bold'>Are you sure you want duplicate this entry?</div>
    </ControlledConfirmationPopover>
  </>
}

export function IdAndNameAndCreateDateFormatter({ info }) {
  const { row } = getInfo(info)
  const { column: { id: field } } = info
  const { id } = row
  return <>
    <div className='twy-text-base-accent'>#{id} - {row[field]}</div>
    <span className='text-sm'>
      {formatDate(row.created_at, 'MMM DD, YYYY - hh:mm A')}
    </span>
  </>
}

export function NameAndCreateDateFormatter({ info, link = true }) {
  const { definitions: { basePath, entityNameModule, entityNamePlural } } = useGrid()
  const { row } = getInfo(info)
  const { column: { id: field } } = info
  const editLink = link ? buildEditLink({ basePath, entityNameModule, entityNamePlural, row }) : null

  return <NameAndLabelFormatter title={row[field]} subtitle={formatDate(row.created_at, 'MMM DD, YYYY - hh:mm A')} editLink={editLink} />
}

export function NameAndLabelFormatter({ title, subtitle, editLink }) {
  return <>
    <Link to={editLink}>
      <TruncateWithTooltipFormatter value={title} className='twy-text-base-accent hover:underline' />
    </Link>
    <span className='text-sm font-semibold twy-text-base-neutral uppercase'>
      {subtitle}
    </span>
  </ >
}

export function NameAndCreateDateWithUserFormatter({ info, link = true }) {
  const { row } = getInfo(info)
  const { column: { id: field } } = info
  const { definitions: { basePath, entityNameModule, entityNamePlural } } = useGrid()
  const editLink = link ? buildEditLink({ basePath, entityNameModule, entityNamePlural, row }) : null

  return <>
    <Link to={editLink}>
      <TruncateWithTooltipFormatter value={row[field]} className='twy-text-base-accent hover:underline' />
    </Link>
    <span className='text-sm font-semibold twy-text-base-neutral uppercase'>
      Created at: {formatDate(row.created_at || row.createdAt, 'MMM DD, YYYY - hh:mm A')} by {row.user.first_name} {row.user.last_name}
    </span>
  </>
}

export function HeaderRPUImpresisonsFormatter({ title }) {
  const { store: { collections: { items } } } = useGrid()
  const [row] = items || []

  if (!row) {
    return <span>{title}</span>
  }

  return <span className='flex gap-1'>
    {title}
    <InfoTip side='bottom' disabled color='neutral'>
      {row.lastUpdate && <>Last update: {formatDateTime(row.lastUpdate)} <br /></>}
      Updated every 30 minutes
    </InfoTip>
  </span>
}

export function RPUFormatter({ info }) {
  const { value } = getInfo(info)
  return <NumberFormat
    value={value || 0}
    displayType="text"
    thousandSeparator=","
    decimalSeparator="."
    prefix={'$'}
    decimalScale={2}
    fixedDecimalScale={true}
  />
}

export function HighlightedColumn(info) {
  const value = info.getValue()
  return <span className='twy-text-neutral'>{value}</span>
}

export function TokenFormatter({ info }) {
  const { value: token } = getInfo(info)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let timeoutId = ''

    if (copied) {
      timeoutId = setTimeout(() => setCopied(false), 2000)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [copied])

  const handleClick = useCallback(async () => {
    setCopied(true)
    copy(token)
  }, [token])

  return <DefaultTooltip content='Copied!' open={copied}>
    <DefaultTooltip content={!copied && 'Click to copy the token.'}>
      <KeyRound size={16} onClick={handleClick} className='twy-text-accent' />
    </DefaultTooltip>
  </DefaultTooltip>
}

export function buildEditLink({ basePath, entityNameModule, entityNamePlural, row }) {
  let path = basePath
  if (!path) {
    path = `/${entityNameModule}/${entityNamePlural.toLowerCase()}`
  }

  return `${path}/${row.id}`
}

export function TruncateWithTooltipFormatter({ value, className, info, tipProps = {} }) {
  const title = info ? info.getValue() : value
  const titleRef = useRef(null)
  const [showTooltip, setShowTooltip] = useState(false)

  const handleTooltipCheck = useCallback(() => {
    const element = titleRef.current
    if (!element) {
      return
    }

    setShowTooltip(element.scrollWidth > element.clientWidth)
  }, [])

  return <DefaultTooltip
    content={showTooltip ? title : null}
    triggerProps={{ asChild: true }}
    contentProps={{ className: 'break-words!' }}
    {...tipProps}
  >
    <div
      ref={titleRef}
      onMouseEnter={handleTooltipCheck}
      onFocus={handleTooltipCheck}
      className={cn('truncate', className)}
    >
      {title || '--'}
    </div>
  </DefaultTooltip>
}
